/**
 * Beta config service (BL-022).
 *
 * Fetches the public `GET /api/config/beta` payload — whether the beta
 * banner/feedback CTA and the discount pricing are active. The endpoint
 * is unauthenticated, so this works before sign-in (banner on auth
 * screens) and on all three portals.
 */

import type { ResolvedFlags } from '@tonnie/foundation';
import type { TonnieClient } from '../client/base';

export interface BetaConfig {
  beta: { enabled: boolean };
  pricing: {
    mode: 'off' | 'fixed_price' | 'percentage';
    percentOff?: number;
  };
  /**
   * Mobile store-update thresholds (CalVer strings, or null when unset).
   * The mobile app compares its own version: below `minSupportedVersion`
   * → hard "Update required" blocker; below `latestVersion` → soft
   * "Update available" banner. Optional so older backends (pre-field) and
   * the web portals — which ignore it — stay compatible.
   */
  mobile?: { minSupportedVersion: string | null; latestVersion: string | null };
  /**
   * Resolved feature flags (W0-PLAN §13.5) — the earliest boot-time lever a
   * shipped mobile binary too old to call `GET /api/config/flags` itself
   * has over this release's flags. Optional so an older backend (pre-field)
   * stays compatible; callers that need flags before this field existed
   * should keep using `useFeatureFlags()`.
   */
  flags?: ResolvedFlags;
}

export class BetaConfigService {
  constructor(private readonly client: TonnieClient) {}

  async getBetaConfig(): Promise<BetaConfig> {
    return this.client.get<BetaConfig>('/api/config/beta');
  }
}
